'use client';
import React, { useState, useEffect, useRef } from 'react';

const mainNavLinks = [
  { label: 'About Us', href: '#about' },
  { label: 'Our Story', href: '#our-story' },
  { label: 'Projects', href: '#projects' },
  { label: 'Why Us', href: '#why-choose' },
];

const moreNavLinks = [
  { label: 'Blog', href: '#blogs' },
  { label: 'Admin', href: '/admin' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-brand-dark shadow-nav' : ''
      }`}
      style={!scrolled ? { background: 'rgba(0,0,0,0.25)' } : {}}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-1 flex-shrink-0">
            <img
              src="/assets/images/ChatGPT_Image_Aug_8__2026__05_21_44_PM-1786189960912.png"
              alt="Neo Capital Group Logo"
              className="h-16 md:h-20 w-auto object-contain mix-blend-screen brightness-110"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-bold font-lato tracking-wide" style={{ color: '#FFD54D', fontSize: '21px', fontWeight: 700 }}>Neo Capital Group</span>
              <span className="text-white font-lato tracking-wide" style={{ fontSize: '15px', fontWeight: 500 }}>Upscaling Wealth. Transforming New Horizons.</span>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-5">
            {mainNavLinks?.map((link) => (
              <a
                key={link?.label}
                href={link?.href}
                className="text-white font-lato uppercase tracking-wider hover:text-brand-turmeric transition-colors duration-200"
                style={{ fontSize: '15px', fontWeight: 500 }}
              >
                {link?.label}
              </a>
            ))}

            {/* More dropdown (3-line / hamburger) */}
            <div className="relative" ref={moreRef}>
              <button
                onClick={() => setMoreOpen(!moreOpen)}
                className="flex flex-col justify-center items-center gap-1 p-2 text-white hover:text-brand-turmeric transition-colors duration-200"
                aria-label="More options"
              >
                <span className="block w-5 h-0.5 bg-current"></span>
                <span className="block w-5 h-0.5 bg-current"></span>
                <span className="block w-5 h-0.5 bg-current"></span>
              </button>

              {moreOpen && (
                <div className="absolute right-0 top-full mt-2 w-40 bg-brand-dark border border-white/10 rounded-lg shadow-lg overflow-hidden z-50">
                  {moreNavLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={() => setMoreOpen(false)}
                      className="block px-4 py-3 text-white font-lato uppercase tracking-wide hover:text-brand-turmeric hover:bg-white/5 transition-colors duration-200"
                      style={{ fontSize: '15px', fontWeight: 500 }}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <a
              href="#contact"
              className="hidden md:inline-flex items-center px-5 py-2 text-white rounded-lg font-lato hover:opacity-90 transition-opacity duration-200"
              style={{ backgroundColor: '#D9A514', fontSize: '15px', fontWeight: 600 }}
            >
              Contact Us
            </a>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden text-white p-2"
              aria-label="Toggle menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-brand-dark border-t border-white/10 py-4">
            {mainNavLinks?.map((link) => (
              <a
                key={link?.label}
                href={link?.href}
                onClick={() => setMobileOpen(false)}
                className="block px-4 py-3 text-white font-lato hover:text-brand-turmeric hover:bg-white/5 transition-colors"
                style={{ fontSize: '15px', fontWeight: 500 }}
              >
                {link?.label}
              </a>
            ))}
            <div className="border-t border-white/10 mt-2 pt-2">
              {moreNavLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block px-4 py-3 text-white font-lato hover:text-brand-turmeric hover:bg-white/5 transition-colors"
                  style={{ fontSize: '15px', fontWeight: 500 }}
                >
                  {link.label}
                </a>
              ))}
            </div>
            <div className="px-4 pt-3">
              <a
                href="#contact"
                className="block text-center px-5 py-2 text-white rounded-lg font-lato"
                style={{ backgroundColor: '#D9A514', fontSize: '15px', fontWeight: 600 }}
              >
                Contact Us
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
